import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, BlockType, Define, ValidationMessage, ValidationMessageType, TibboDataType,
    CodeBlock,
    SettingState,
    SettingOption,
} from '@appblocks/codegeneration/types';
import * as zephyr from './zephyr';

export default class Net extends BaseFeature {
    constructor() {
        super();
        this.name = 'Ethernet';
        this.id = 'net';
        this.priority = 99;
        this.icon = 'MdSettingsEthernet';
        this.requirements = [
            'ethernet',
        ];
        this.capabilities = [
            'network',
        ];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'DHCP',
                name: 'DHCP',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'NETCP',
                    NAME: 'Ethernet DHCP',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: 0,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'Enabled',
                            value: '1',
                        },
                    ],
                },
                default: '1',
                required: true,
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'IP',
                name: 'IP Address',
                settingType: SettingType.IP,
                required: false,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'NETIP',
                    NAME: 'Ethernet IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                default: '1.0.0.1',
            },
            {
                id: 'NM',
                name: 'Netmask',
                settingType: SettingType.IP,
                required: false,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'NETNM',
                    NAME: 'Ethernet Netmask',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '255.255.255.0',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                default: '255.255.255.0',
            },
            {
                id: 'GI',
                name: 'Gateway',
                settingType: SettingType.IP,
                required: false,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'NETGW',
                    NAME: 'Ethernet Gateway',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                default: '1.0.0.1',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getState(settingId: string): SettingState {
        if (settingId === 'ENABLED') {
            return SettingState.ENABLED;
        }
        const ENABLED = this.getEnabled();
        if (!ENABLED) {
            return SettingState.DISABLED;
        }
        return super.getState(settingId);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'ENABLED') {
            if (this.project.runtime === 'tios') {
                return [
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ];
            }
        }
        return super.getSettingOptions(settingId);
    }

    getEnabled(): boolean {
        if (this.project.runtime === 'tios') {
            return true;
        }
        return super.getEnabled();
    }

    getDependencies() {
        const dependencies = [];
        const DHCP = this.getSettingValue('DHCP');
        let DHCPIsLinked = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'DHCP') {
                    DHCPIsLinked = true;
                }
            }
        }
        if (this.project.runtime === 'tios') {
            if (DHCP === '1' || DHCPIsLinked) {
                dependencies.push('dhcp');
            }
        }
        return dependencies;
    }

    getCodeBlocks(): CodeBlock[] {
        if (this.project.runtime === 'zephyr') {
            return zephyr.getCodeBlocks(this);
        }
        if (this.project.runtime === 'simulator') {
            return [];
        }

        const blocks = [];
        const DHCP = this.getSettingValue('DHCP');
        const NETMASK = this.getSettingValue('NM');
        const IP = this.getSettingValue('IP');
        const GATEWAY = this.getSettingValue('GI');
        const project = this.project;
        let DHCPIsLinked = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'DHCP') {
                    DHCPIsLinked = true;
                }
            }
        }


        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim device_net_ip as string(16)
dim device_net_mask as string(16)
dim device_net_gateway as string(16)
dim net_dhcp_enabled as boolean`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare device_net_ip as string(16)
declare device_net_mask as string(16)
declare device_net_gateway as string(16)
declare net_dhcp_enabled as boolean`,
        });

        let bootTemplate = `
    'Ethernet setup
    '================================================================
    device_net_ip = ${IP}
    device_net_mask = ${NETMASK}
    device_net_gateway = ${GATEWAY}
    
    dim dhcp_enabled as string(1) = ${DHCP}

    if dhcp_enabled = "1" then
        net_dhcp_enabled = true
    else
        net_dhcp_enabled = false
    end if
    `;

        blocks.push({
            location: 'on_net_link_change',
            blockType: BlockType.CODE,
            template: `
    if net.linkstate=PL_NET_LINKSTAT_NOLINK then
        interface_set(PL_SOCK_INTERFACE_NET,NO)
        '****callback_net_disconnected****'
    else
        if net_dhcp_enabled = false then
            interface_set(PL_SOCK_INTERFACE_NET,YES)
        end if
    end if`,
        });

        if (DHCP === '0' && !DHCPIsLinked) {
            bootTemplate += `
    net.ip=device_net_ip
    net.netmask=device_net_mask
    net.gatewayip=device_net_gateway`;
        } else {
            bootTemplate += `

    if net_dhcp_enabled = false then
        net.ip=device_net_ip
        net.netmask=device_net_mask
        net.gatewayip=device_net_gateway
    else
        'ethernet dhcp
        dhcp_start(PL_SOCK_INTERFACE_NET,device_net_ip,"")
    end if`;

            // write back to setting if linked setting exists
            let ipSettingCode = '';
            const ipSetting = project.getLinkedSetting(this.getID(), 'IP');
            if (ipSetting !== undefined) {
                const settingItem = project.getFeature(ipSetting.getSettingValue('LINKEDSETTING'));
                ipSettingCode += `
var_${settingItem.getSettingValue('SETTINGID')}_set(net.ip)`;
            }
            const gatewaySetting = project.getLinkedSetting(this.getID(), 'GATEWAY');
            if (gatewaySetting !== undefined) {
                const settingItem = project.getFeature(gatewaySetting.getSettingValue('LINKEDSETTING'));
                ipSettingCode += `
    var_${settingItem.getSettingValue('SETTINGID')}_set(net.gatewayip)`;
            }
            const netmaskSetting = project.getLinkedSetting(this.getID(), 'NETMASK');
            if (netmaskSetting !== undefined) {
                const settingItem = project.getFeature(netmaskSetting.getSettingValue('LINKEDSETTING'));
                ipSettingCode += `
    var_${settingItem.getSettingValue('SETTINGID')}_set(net.netmask)`;
            }

            blocks.push({
                location: 'callback_dhcp_ok',
                blockType: BlockType.CODE,
                template: `
    if interface=PL_SOCK_INTERFACE_NET then
        if renew=YES and net.ip<>ip then
            'this is a lease renewal and the DHCP server has issues new IP
            'it is better to reboot than deal with the implications of the changed IP
            sys.reboot
        end if

        #if DEV_DEBUG_PRINT
            dev_debugprint("Ethernet DHCP OK ("+ip+")")
        #endif

        if net.ip<>ip then
            sock.inconenabledmaster=NO
            close_interface_sockets(PL_SOCK_INTERFACE_NET)
            net.ip=ip
            if gateway_ip<>"" then net.gatewayip=gateway_ip
            if netmask<>"" then net.netmask=netmask
            sock.inconenabledmaster=YES
        end if
        ${ipSettingCode}
        interface_set(PL_SOCK_INTERFACE_NET,YES)
    end if
            `,
            });
        }

        bootTemplate += `
    '-----------------------------------------------------------------`;

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootTemplate,
        });

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
if net.linkstate<>PL_NET_LINKSTAT_NOLINK AND net_dhcp_enabled = false then
    interface_set(PL_SOCK_INTERFACE_NET,YES)
end if`,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_net';
    }
}
